import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, apiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { ShieldCheck, Loader2, Eye, EyeOff } from "lucide-react";
import { toast } from "sonner";
import { firstAllowedPath } from "@/lib/landing";

export default function Setup() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [checking, setChecking] = useState(true);
  const [form, setForm] = useState({ name: "", email: "", password: "", business_name: "" });
  const [show, setShow] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    api.get("/auth/setup-status")
      .then((r) => { if (!r.data.needs_setup) navigate("/", { replace: true }); })
      .catch(() => {})
      .finally(() => setChecking(false));
  }, [navigate]);

  function upd(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function submit(e) {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/auth/setup", {
        name: form.name.trim(), email: form.email.trim(),
        password: form.password, business_name: form.business_name.trim() || "My Business",
      });
      await setSession(data.access_token, data.user);
      toast.success("Setup complete. Welcome!");
      navigate(firstAllowedPath(data.user), { replace: true });
    } catch (err) { setError(apiError(err)); }
    setLoading(false);
  }

  if (checking) return <div className="min-h-screen brand-grid-bg" />;

  return (
    <div className="min-h-screen brand-grid-bg flex items-center justify-center p-5">
      <div className="w-full max-w-md">
        <div className="bg-white rounded-2xl shadow-xl border border-slate-100 overflow-hidden">
          <div className="p-7 sm:p-8">
            <div className="flex items-center gap-3 mb-6">
              <div className="bg-slate-50 rounded-xl p-1.5 ring-1 ring-slate-100"><Logo size={40} /></div>
              <div>
                <div className="font-display font-extrabold text-lg leading-none"><span className="text-[#1A2A5E]">Bill</span><span className="text-[#F4571E]">One</span></div>
                <div className="text-[8px] tracking-[0.2em] text-slate-400 font-semibold mt-1">FIRST-TIME SETUP</div>
              </div>
            </div>

            <div className="flex items-center gap-3 mb-6">
              <div className="h-11 w-11 rounded-lg flex items-center justify-center bg-[#1A2A5E]/10"><ShieldCheck className="h-6 w-6 text-[#1A2A5E]" /></div>
              <div>
                <h1 className="font-display text-xl font-bold text-[#0F172A]">Create AAdmin Account</h1>
                <p className="text-xs text-slate-400">This is the super admin who controls everything</p>
              </div>
            </div>

            <form onSubmit={submit} className="space-y-4" data-testid="setup-form">
              <div>
                <Label className="text-slate-600">Your Name</Label>
                <Input data-testid="setup-name" value={form.name} onChange={(e) => upd("name", e.target.value)} placeholder="Full name" className="mt-1.5" autoFocus required />
              </div>
              <div>
                <Label className="text-slate-600">Business Name</Label>
                <Input data-testid="setup-business" value={form.business_name} onChange={(e) => upd("business_name", e.target.value)} placeholder="My Store" className="mt-1.5" />
              </div>
              <div>
                <Label className="text-slate-600">Email</Label>
                <Input data-testid="setup-email" type="email" value={form.email} onChange={(e) => upd("email", e.target.value)} placeholder="you@store.com" className="mt-1.5" required />
              </div>
              <div>
                <Label className="text-slate-600">Password</Label>
                <div className="relative mt-1.5">
                  <Input data-testid="setup-password" type={show ? "text" : "password"} value={form.password} onChange={(e) => upd("password", e.target.value)} placeholder="••••••••" required minLength={6} />
                  <button type="button" onClick={() => setShow(!show)} className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400">
                    {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                  </button>
                </div>
              </div>
              {error && <div data-testid="setup-error" className="text-sm text-red-600 bg-red-50 border border-red-100 rounded-lg px-3 py-2">{error}</div>}
              <Button type="submit" data-testid="setup-submit" disabled={loading} className="w-full h-11 text-base font-semibold bg-[#1A2A5E]">
                {loading ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Setting up...</> : "Complete Setup"}
              </Button>
            </form>
          </div>
        </div>
        <p className="text-center text-xs text-slate-400 mt-6">Powered by <span className="font-semibold text-slate-500">ACore</span></p>
      </div>
    </div>
  );
}
